<template>
  <super-admin-layout>
  <v-container fluid class="text-left pa-6">
    <v-row>
      <v-col cols="12">
        <h1 class="rollout-title">Course Rollout</h1>
        <p class="rollout-sub">
          Push a course to customers, or remove one you pushed earlier.
          Preview shows exactly what will happen, company by company, before
          anything is written. Every apply is recorded in the audit trail below.
        </p>
      </v-col>
    </v-row>

    <v-row>
      <!-- ============ the form ============ -->
      <v-col cols="12" md="5">
        <v-card outlined class="pa-4">
          <div class="section-label">What to roll out</div>
          <v-btn-toggle v-model="entityType" mandatory dense class="mb-4" @change="onEntityChange">
            <v-btn value="course" text>Course</v-btn>
            <v-btn value="playlist" text>Playlist</v-btn>
          </v-btn-toggle>

          <div class="section-label">Source {{ entityType }}</div>
          <v-autocomplete
            v-model="sourceCompany"
            :items="companies"
            item-text="company_name"
            item-value="id"
            :label="'Company that owns the ' + entityType"
            outlined dense
            @change="loadSources"
          />
          <v-autocomplete
            v-model="courseId"
            :items="entityType === 'playlist' ? playlists : courses"
            :item-text="entityType === 'playlist' ? playlistLabel : 'name'"
            item-value="id"
            :label="entityType === 'playlist' ? 'Playlist (with course count)' : 'Course'"
            outlined dense
            :loading="loadingCourses"
            :disabled="!sourceCompany"
            @change="invalidatePreview"
          />
          <p v-if="entityType === 'playlist'" class="hint-text mb-3">
            Pushing a playlist also brings its member courses into each
            company - ones they already have are matched, missing ones are
            copied. Removing a playlist never removes the courses.
          </p>

          <div class="section-label mt-2">Action</div>
          <v-btn-toggle v-model="action" mandatory dense class="mb-4" @change="invalidatePreview">
            <v-btn value="push" text>Push to customers</v-btn>
            <v-btn value="remove" text>Remove from customers</v-btn>
          </v-btn-toggle>

          <div class="section-label">Targets</div>
          <v-radio-group v-model="targets" dense @change="invalidatePreview">
            <v-radio label="All active customers" value="active" />
            <v-radio label="Selected companies only" value="selected" />
          </v-radio-group>
          <v-autocomplete
            v-if="targets === 'selected'"
            v-model="companyIds"
            :items="companies"
            item-text="company_name"
            item-value="id"
            label="Companies"
            multiple chips small-chips deletable-chips outlined dense
            @change="invalidatePreview"
          />

          <v-checkbox
            v-if="action === 'push'"
            v-model="publish"
            label="Publish immediately (unpublished copies are invisible to learners)"
            dense
            @change="invalidatePreview"
          />

          <div class="d-flex align-center mt-2">
            <v-btn
              color="secondary"
              outlined
              :loading="previewing"
              :disabled="!courseId"
              @click="preview"
            >
              Preview
            </v-btn>
            <v-btn
              color="primary"
              depressed
              class="ml-3"
              :loading="applying"
              :disabled="!previewResult"
              @click="confirmDialog = true"
            >
              Apply
            </v-btn>
            <span v-if="!previewResult && courseId" class="ml-3 hint-text">
              Preview first - Apply unlocks after it.
            </span>
          </div>
        </v-card>
      </v-col>

      <!-- ============ the preview ============ -->
      <v-col cols="12" md="7">
        <v-card outlined class="pa-4" v-if="previewResult">
          <div class="section-label">
            Preview: {{ previewResult.course.name }} ·
            {{ previewResult.action === 'remove' ? 'remove from' : 'push to' }}
            {{ previewResult.targets }} companies
          </div>
          <div class="mb-3">
            <v-chip
              v-for="(count, outcome) in nonZeroTotals"
              :key="outcome"
              class="mr-2 mb-1"
              :color="chipColor(outcome)"
              small outlined
            >
              {{ outcomeLabel(outcome) }}: {{ count }}
            </v-chip>
          </div>
          <v-data-table
            :headers="previewHeaders"
            :items="previewResult.companies"
            :items-per-page="10"
            dense
          >
            <template v-slot:item.outcome="{ item }">
              <v-chip :color="chipColor(item.outcome)" x-small outlined>
                {{ outcomeLabel(item.outcome) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
        <v-card outlined class="pa-8 text-center grey--text" v-else>
          Run a preview to see the company-by-company plan here.
        </v-card>
      </v-col>
    </v-row>

    <!-- ============ audit trail ============ -->
    <v-row>
      <v-col cols="12">
        <v-card outlined class="pa-4">
          <div class="section-label">Rollout history</div>
          <v-data-table
            :headers="historyHeaders"
            :items="rollouts"
            :items-per-page="10"
            dense
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="item.status === 'done' ? 'success' : (item.status === 'done_with_failures' ? 'warning' : 'info')"
                x-small outlined
              >{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.totals="{ item }">
              <span v-if="item.totals" class="totals-text">{{ totalsSummary(item.totals) }}</span>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- ============ confirm ============ -->
    <v-dialog v-model="confirmDialog" max-width="480">
      <v-card>
        <v-card-title>Confirm rollout</v-card-title>
        <v-card-text v-if="previewResult">
          <strong>{{ previewResult.course.name }}</strong> will be
          {{ previewResult.action === 'remove' ? 'removed from' : 'pushed to' }}
          <strong>{{ affectedCount }}</strong> of {{ previewResult.targets }} companies
          ({{ skippedCount }} skipped). This runs in the background and is audited.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="applying" @click="apply">
            Yes, apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  </super-admin-layout>
</template>

<script>
import SuperAdminLayout from "@/layouts/SuperAdmin";
import superAdminAPI from "@/api/superadmin.js";
import companyAPI from "@/api/company.js";
import courseAPI from "@/api/course.js";

export default {
  name: "CourseRollout",
  components: {
    SuperAdminLayout,
  },
  data() {
    return {
      companies: [],
      courses: [],
      playlists: [],
      entityType: "course",
      sourceCompany: null,
      courseId: null,
      action: "push",
      targets: "active",
      companyIds: [],
      publish: true,
      previewing: false,
      applying: false,
      loadingCourses: false,
      previewResult: null,
      rollouts: [],
      confirmDialog: false,
      pollTimer: null,
      previewHeaders: [
        { text: "Company", value: "company_name" },
        { text: "Outcome", value: "outcome" }
      ],
      historyHeaders: [
        { text: "When", value: "created_at" },
        { text: "Type", value: "entity_type" },
        { text: "Source", value: "source_name" },
        { text: "Action", value: "action" },
        { text: "Status", value: "status" },
        { text: "Result", value: "totals", sortable: false }
      ]
    };
  },
  computed: {
    nonZeroTotals() {
      if (!this.previewResult) return {};
      const out = {};
      Object.entries(this.previewResult.totals).forEach(([k, v]) => {
        if (v > 0) out[k] = v;
      });
      return out;
    },
    affectedCount() {
      const t = this.previewResult ? this.previewResult.totals : {};
      return (t.copy || 0) + (t.restore || 0) + (t.remove || 0);
    },
    skippedCount() {
      const t = this.previewResult ? this.previewResult.totals : {};
      return (t.skipped_marker || 0) + (t.skipped_name || 0) + (t.none_found || 0);
    }
  },
  mounted() {
    this.loadCompanies();
    this.loadRollouts();
    // Poll while anything is in flight so the history row fills in live.
    this.pollTimer = setInterval(() => {
      if (this.rollouts.some(r => r.status === "queued" || r.status === "running")) {
        this.loadRollouts();
      }
    }, 5000);
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
  },
  methods: {
    loadCompanies() {
      companyAPI.getCompanies("?limit=1000&page=1", "", "", "", "").then(resp => {
        this.companies = (resp.data && resp.data.data) || [];
        // Default the source to the template company where Vik curates.
        const template = this.companies.find(c => c.company_name === "DefaultCompany");
        if (template) {
          this.sourceCompany = template.id;
          this.loadSources();
        }
      });
    },
    onEntityChange() {
      this.courseId = null;
      this.invalidatePreview();
      this.loadSources();
    },
    playlistLabel(item) {
      return item.name + " (" + item.courses_count + " courses)";
    },
    loadSources() {
      this.courses = [];
      this.playlists = [];
      this.courseId = null;
      this.invalidatePreview();
      if (!this.sourceCompany) return;
      this.loadingCourses = true;
      const req =
        this.entityType === "playlist"
          ? superAdminAPI.getRolloutPlaylists(this.sourceCompany)
          : courseAPI.getCoursesData("?limit=2000&page=1&id=" + this.sourceCompany, "");
      req
        .then(resp => {
          const rows = (resp.data && resp.data.data) || [];
          if (this.entityType === "playlist") this.playlists = rows;
          else this.courses = rows;
        })
        .finally(() => {
          this.loadingCourses = false;
        });
    },
    invalidatePreview() {
      // Any input change makes the previous preview stale; Apply re-locks
      // until the super admin has looked at the new plan.
      this.previewResult = null;
    },
    payload() {
      return {
        source_id: this.courseId,
        entity_type: this.entityType,
        rollout_action: this.action,
        targets: this.targets,
        company_ids: this.targets === "selected" ? this.companyIds : [],
        publish: this.publish
      };
    },
    preview() {
      this.previewing = true;
      superAdminAPI
        .previewCourseRollout(this.payload())
        .then(resp => {
          this.previewResult = resp.data.data;
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message) || "Preview failed.";
          this.$snotify.error(msg);
        })
        .finally(() => {
          this.previewing = false;
        });
    },
    apply() {
      this.applying = true;
      superAdminAPI
        .applyCourseRollout(Object.assign(this.payload(), { confirmed: true }))
        .then(resp => {
          const d = resp.data.data;
          this.$snotify.success(
            "Rollout #" + d.rollout_id + " queued for " + d.targets + " companies."
          );
          this.confirmDialog = false;
          this.invalidatePreview();
          this.loadRollouts();
        })
        .catch(err => {
          const msg = (err.response && err.response.data && err.response.data.message) || "Apply failed.";
          this.$snotify.error(msg);
        })
        .finally(() => {
          this.applying = false;
        });
    },
    loadRollouts() {
      superAdminAPI.getCourseRollouts().then(resp => {
        this.rollouts = (resp.data && resp.data.data) || [];
      });
    },
    outcomeLabel(outcome) {
      return {
        copy: "Will copy",
        restore: "Will restore",
        skipped_marker: "Already rolled out",
        skipped_name: "Name clash - skipped",
        remove: "Will remove",
        none_found: "Nothing to remove"
      }[outcome] || outcome;
    },
    chipColor(outcome) {
      return {
        copy: "success",
        restore: "success",
        remove: "error",
        skipped_marker: "grey",
        skipped_name: "warning",
        none_found: "grey"
      }[outcome] || "grey";
    },
    totalsSummary(t) {
      return Object.entries(t)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => k + ": " + v)
        .join(", ");
    }
  }
};
</script>

<style scoped>
.rollout-title {
  font-size: 26px;
  font-weight: 700;
}
.rollout-sub {
  color: #5a6a7a;
  max-width: 720px;
}
.section-label {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a8794;
  margin-bottom: 8px;
}
.hint-text {
  font-size: 12px;
  color: #7a8794;
}
.totals-text {
  font-size: 12px;
}
</style>
